import { useCallback } from "react";
import client from "libs/client";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import { DISCOVER_GAME_HISTORY_NOTES_QUERY_KEY } from "./constants";

async function invalidateGameHistoryNotes(): Promise<void> {
	await client.queryClient.invalidateQueries({
		predicate: (query) => query.queryKey.includes(DISCOVER_GAME_HISTORY_NOTES_QUERY_KEY),
	});
}

interface UseGameHistoryNoteMutationsOptions {
	gameId: number;
	resetLoadedOlderPages: () => void;
	scrollFeedToBottom: () => void;
	onPostSuccess?: () => void;
	onPatchSuccess?: () => void;
}

export function useGameHistoryNoteMutations({
	gameId,
	resetLoadedOlderPages,
	scrollFeedToBottom,
	onPostSuccess,
	onPatchSuccess,
}: UseGameHistoryNoteMutationsOptions) {
	const { addSuccessToast, addErrorToast } = useToast();

	const afterMutationSuccess = useCallback(async () => {
		resetLoadedOlderPages();
		await invalidateGameHistoryNotes();
		scrollFeedToBottom();
	}, [resetLoadedOlderPages, scrollFeedToBottom]);

	const postMutation = client.postDiscoverGameHistoryNote.useMutation({
		onSuccess: async () => {
			onPostSuccess?.();
			await afterMutationSuccess();
			addSuccessToast(_("discover.gameDetail.history.noteAdded"));
		},
		onError: () => {
			addErrorToast(_("discover.gameDetail.history.noteAddFailed"));
		},
	});

	const patchMutation = client.patchDiscoverGameHistoryNote.useMutation({
		onSuccess: async () => {
			onPatchSuccess?.();
			await afterMutationSuccess();
			addSuccessToast(_("discover.gameDetail.history.noteUpdated"));
		},
		onError: () => {
			addErrorToast(_("discover.gameDetail.history.noteUpdateFailed"));
		},
	});

	const deleteMutation = client.deleteDiscoverGameHistoryNote.useMutation({
		onSuccess: async () => {
			await afterMutationSuccess();
			addSuccessToast(_("discover.gameDetail.history.noteDeleted"));
		},
		onError: () => {
			addErrorToast(_("discover.gameDetail.history.noteDeleteFailed"));
		},
	});

	return { postMutation, patchMutation, deleteMutation };
}
